// Two Rolodex accounts that are the same person (e.g. one made by signing in with a work address, one
// with a personal address) are merged in Administration › Users: the one being merged away is removed,
// what it held moves to the one kept, and its address becomes another way to sign in to the kept
// account (a sign-in alias, worker db/aliases.ts). These are the shared rules and request shapes.
import { z } from 'zod';
export const MergeUsersSchema = z.object({
    /** the account merged away (removed). */
    source: z.string().email(),
    /** the account kept. */
    target: z.string().email(),
});
export const RemoveAliasSchema = z.object({ alias: z.string().email() });
export const emptyCarryOver = () => ({
    conversations: 0, savedMessages: 0, groups: 0, groupsJoined: 0, messages: 0, preferences: 0, agenda: 0,
    shares: 0, notifications: 0, filters: 0, pins: 0, orders: 0, notifyLists: 0, employees: 0,
});
/** two carry-overs added up (the repair runs one per merged address). */
export function addCarryOver(a, b) {
    const out = { ...a };
    for (const k of Object.keys(out))
        out[k] += b[k];
    return out;
}
// lower case, no accents or punctuation, Greek final sigma folded: "Γ. Φούντας" → "γ φουντασ"
const plain = (s) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/ς/g, 'σ').replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
/** Levenshtein distance, capped (we only care whether it's small). */
export function editDistance(a, b, cap = 3) {
    if (Math.abs(a.length - b.length) > cap)
        return cap + 1;
    let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
    for (let i = 1; i <= a.length; i++) {
        const cur = [i];
        let best = i;
        for (let j = 1; j <= b.length; j++) {
            cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
            best = Math.min(best, cur[j]);
        }
        if (best > cap)
            return cap + 1;
        prev = cur;
    }
    return prev[b.length];
}
/** the parts of a name worth comparing: initial of the first name and the surname (the last word). */
function nameParts(p) {
    const words = plain(p.displayName || '').split(' ').filter(Boolean);
    if (words.length < 2)
        return null;
    return { first: words[0].charAt(0), last: words[words.length - 1] };
}
/** a mailbox name compared without digits, dots and the like: "gio.fountas01" → "giofountas". */
const mailbox = (email) => plain(email.split('@')[0] ?? '').replace(/[^\p{L}]+/gu, '');
/** could these two accounts be the same person? Same first initial and a surname a letter or two apart
 *  ("G. Foudas" / "G. Fountas"), or near-identical mailbox names at different domains. A hint for an
 *  admin, never acted on by itself. */
export function likelySamePerson(a, b) {
    if (a.email.toLowerCase() === b.email.toLowerCase())
        return false;
    const na = nameParts(a);
    const nb = nameParts(b);
    if (na && nb && na.first === nb.first && na.last.length >= 4 && editDistance(na.last, nb.last, 2) <= 2)
        return true;
    const ma = mailbox(a.email);
    const mb = mailbox(b.email);
    return ma.length >= 6 && mb.length >= 6 && a.email.split('@')[1] !== b.email.split('@')[1] && editDistance(ma, mb, 2) <= 2;
}
/** pairs of accounts that may be the same person, each pair once. */
export function likelyDuplicates(people) {
    const out = [];
    for (let i = 0; i < people.length; i++) {
        for (let j = i + 1; j < people.length; j++) {
            if (likelySamePerson(people[i], people[j]))
                out.push([people[i], people[j]]);
        }
    }
    return out;
}
/** which of two accounts to keep by default: the one at the company domain, else the one used most recently. */
export function suggestedKeeper(a, b, companyDomain = 'technohull.com') {
    const atCompany = (p) => p.email.toLowerCase().endsWith(`@${companyDomain}`);
    if (atCompany(a) !== atCompany(b))
        return atCompany(a) ? a : b;
    return (b.lastSeenAt ?? '') > (a.lastSeenAt ?? '') ? b : a;
}
// ── carrying the person's data over (worker db/carryOver.ts) ────────────────────────────────────────
const sameAddress = (a, b) => a.trim().toLowerCase() === b.trim().toLowerCase();
/** every list of strings inside a JSON value (a notification's recipients, an agenda entry's people) with
 *  the address `from` replaced by `to`, and `to` listed once. Only whole entries that are that address
 *  change, in any case: an entry that merely contains it, a name fragment, any other text and anything
 *  outside a list stay as they are. `lists` is how many lists changed (0: `value` comes back as it was). */
export function replaceInLists(value, from, to) {
    let lists = 0;
    const walk = (v) => {
        if (Array.isArray(v)) {
            if (!v.some((x) => typeof x === 'string' && sameAddress(x, from)))
                return v.map(walk);
            lists++;
            let listed = false;
            return v
                .map((x) => (typeof x === 'string' && sameAddress(x, from) ? to : walk(x)))
                .filter((x) => {
                if (typeof x !== 'string' || !sameAddress(x, to))
                    return true;
                if (listed)
                    return false; // already in the list (it was there, or `from` was listed twice)
                listed = true;
                return true;
            });
        }
        if (v && typeof v === 'object')
            return Object.fromEntries(Object.entries(v).map(([k, x]) => [k, walk(x)]));
        return v;
    };
    const next = walk(value);
    return lists ? { value: next, lists } : { value, lists: 0 };
}
/** the kept account's settings, with the merged-away account's filling in what it hasn't chosen, and the
 *  later of the two "announcements seen" marks (so nothing either account dismissed pops up again).
 *  `taken` names the settings that came from the merged-away account. */
export function mergePreferences(kept, away) {
    const value = { ...(kept ?? {}) };
    const taken = [];
    for (const [k, v] of Object.entries(away ?? {})) {
        if (v == null || value[k] != null)
            continue;
        value[k] = v;
        taken.push(k);
    }
    const seen = Number(away?.lastSeenAnnId) || 0;
    if (seen > (Number(value.lastSeenAnnId) || 0)) {
        value.lastSeenAnnId = seen;
        if (!taken.includes('lastSeenAnnId'))
            taken.push('lastSeenAnnId');
    }
    return { value, taken };
}
/** the title of a person's conversation with themselves: what a chat between two merged accounts becomes. */
export const SAVED_MESSAGES = 'Saved messages';
/** how conversations come together once merged addresses are their accounts: all of those between the
 *  same two people end in the one the chat itself opens for them (its id is made from both chat ids,
 *  `uidOf`), and one whose two sides are now the same person becomes their "Saved messages", or goes when
 *  it's empty. An untitled group chat left with two people joins theirs too: the chat names it after the
 *  other person, just like their one-to-one conversation. A group with a title, or with three or more
 *  people, stays as it is. A target id still held by a conversation that has to move first waits for the
 *  next pass. */
export function planChatFolds(threads, uidOf) {
    const keyOf = (t) => {
        if (t.isGroup && t.title)
            return null;
        const users = [...new Set(t.members.map((m) => m.user.toLowerCase()))];
        if (users.length === 2)
            return `dm:${users.map(uidOf).sort().join('_')}`;
        if (users.length === 1)
            return `self:${uidOf(users[0])}`;
        return null; // nobody left in it, or more than two: not a one-to-one conversation to sort out
    };
    const groups = new Map();
    for (const t of threads) {
        const key = keyOf(t);
        if (key)
            groups.set(key, [...(groups.get(key) ?? []), t]);
    }
    const ids = new Set(threads.map((t) => t.id));
    const plan = { folds: [], remove: [] };
    for (const [key, group] of groups) {
        const self = key.startsWith('self:');
        if (self && group.every((t) => t.messages === 0)) {
            plan.remove.push(...group.map((t) => t.id));
            continue;
        }
        const inPlace = group.find((t) => t.id === key);
        if (inPlace && group.length === 1 && (!self || inPlace.title === SAVED_MESSAGES))
            continue; // already right
        if (!inPlace && ids.has(key))
            continue; // its id is still another conversation's, which moves first
        const users = [...new Set(group.flatMap((t) => t.members.map((m) => m.user.toLowerCase())))];
        const members = users.map((user) => {
            const reads = {};
            for (const t of group) {
                for (const m of t.members)
                    if (m.user.toLowerCase() === user)
                        reads[t.id] = Math.max(reads[t.id] ?? 0, m.lastReadId);
            }
            return { user, reads };
        });
        plan.folds.push({
            target: key,
            create: !inPlace,
            from: group.filter((t) => t !== inPlace).sort((a, b) => Number(a.isGroup) - Number(b.isGroup)).map((t) => t.id),
            members,
            lastMessageAt: Math.max(0, ...group.map((t) => t.lastMessageAt)),
            title: self ? SAVED_MESSAGES : null,
        });
    }
    return plan;
}
/** where someone's read position goes when conversations are brought together. Each message from someone
 *  else was read or not where it was written (`reads`: how far they had read in each conversation; a
 *  message of their own there shows they had read up to it, as writing moves the position on, even where
 *  an earlier merge lost how far they had read), and one position in the joined history has to stand for
 *  all of them: the one that gets the most of them right, so the fewest read messages show as new again
 *  and the fewest unread ones are hidden. On a tie it's the earlier one (better seen twice than missed),
 *  and never further than they had read anywhere. */
export function joinedReadPosition(user, reads, log) {
    const me = user.toLowerCase();
    const seen = { ...reads };
    for (const m of log)
        if (m.from.toLowerCase() === me)
            seen[m.thread] = Math.max(seen[m.thread] ?? 0, m.id);
    const theirs = log.filter((m) => m.from.toLowerCase() !== me).sort((a, b) => a.id - b.id);
    const wasRead = (m) => m.id <= (seen[m.thread] ?? 0);
    // read up to the very start, every message they had read would show as new; each step past one they
    // had read puts one right, each past one they hadn't hides it
    let wrong = theirs.filter(wasRead).length;
    let fewest = wrong;
    let next = theirs[0]?.id ?? Infinity; // the best position is just before this message
    theirs.forEach((m, i) => {
        wrong += wasRead(m) ? -1 : 1;
        if (wrong < fewest) {
            fewest = wrong;
            next = theirs[i + 1]?.id ?? Infinity;
        }
    });
    return Math.max(0, Math.min(Math.max(0, ...Object.values(seen)), next - 1));
}
const count = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const andList = (xs) => (xs.length < 2 ? xs.join('') : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`);
/** what a merge did, in plain words for the admin: a line for each kind of thing that moved. */
export function describeMerge(s, who, roleLabel = (r) => r) {
    const lines = [];
    if (s.roles.length)
        lines.push(`${s.roles.length === 1 ? 'Role' : 'Roles'} added: ${andList(s.roles.map(roleLabel))}.`);
    if (s.departments)
        lines.push(`Added to ${count(s.departments, 'department')}.`);
    if (s.conversations) {
        lines.push(`${count(s.conversations, 'conversation')} moved over, one per person: where both accounts talked to someone, the two are now one.`);
    }
    if (s.savedMessages) {
        lines.push(s.savedMessages === 1
            ? `The conversation between the two accounts is now ${who.kept}'s "${SAVED_MESSAGES}".`
            : `${s.savedMessages} conversations between the two accounts are now ${who.kept}'s "${SAVED_MESSAGES}".`);
    }
    if (s.groups)
        lines.push(`${count(s.groups, 'group chat')} moved over, with ${who.kept} in ${s.groups === 1 ? 'it' : 'each'} once.`);
    if (s.groupsJoined) {
        lines.push(`${count(s.groupsJoined, 'group chat')} left with just ${who.kept} and one other person ${s.groupsJoined === 1 ? 'is now part of their conversation' : 'are now part of their conversations'}.`);
    }
    if (s.messages)
        lines.push(`${count(s.messages, 'message')} sent as ${who.awayEmail} now ${s.messages === 1 ? 'shows' : 'show'} as ${who.kept}'s.`);
    if (s.preferences)
        lines.push(`${count(s.preferences, 'setting')} ${who.kept} hadn't chosen, taken from ${who.away}.`);
    if (s.agenda)
        lines.push(`Agenda: ${count(s.agenda, 'entry or day note', 'entries and day notes')}.`);
    if (s.shares) {
        lines.push(`${count(s.shares, 'agenda entry', 'agenda entries')} shared with ${who.awayEmail} ${s.shares === 1 ? 'is' : 'are'} now shared with ${who.kept}.`);
    }
    if (s.orders)
        lines.push(`${count(s.orders, 'order')} created as ${who.awayEmail}, now ${who.kept}'s own.`);
    if (s.pins)
        lines.push(`${count(s.pins, 'pin')} on the pin board.`);
    if (s.filters)
        lines.push(`${count(s.filters, 'saved filter')}.`);
    if (s.notifications)
        lines.push(`${count(s.notifications, 'notification')} in the bell.`);
    if (s.notifyLists) {
        lines.push(`${count(s.notifyLists, 'notification list')} (who gets an email, who's left out) now ${s.notifyLists === 1 ? 'names' : 'name'} ${who.keptEmail} instead.`);
    }
    if (s.employees) {
        lines.push(`${s.employees === 1 ? 'The employee record' : `${s.employees} employee records`} with ${who.awayEmail} now ${s.employees === 1 ? 'has' : 'have'} ${who.keptEmail}.`);
    }
    if (s.password)
        lines.push(`${who.kept} had no password and now signs in with ${who.away}'s.`);
    return lines;
}
//# sourceMappingURL=userMerge.js.map